import { ExportIcon } from "@xola/icons";
import { JsDateRange, now } from "common";
import { capitalize } from "lodash";
import React, { useState } from "react";
import { useQuery } from "react-query";
import { ExportJob, GratuityStatus, Guide } from "types";
import shallow from "zustand/shallow";
import { api } from "../../../api/api";
import { useSeller } from "../../../hooks/useAccount";
import { useAutoFetchCollection } from "../../../hooks/useAutoFetchCollection";
import { useModal } from "../../../hooks/useVisibility";
import { getInitialDate } from "../../../models/Reports";
import { GratuitiesDataFilter, useGratuitiesFilterStore } from "../../../stores/reports/gratuitiesFilterStore";
import { ExportJobModal } from "../../Common/ExportJobModal";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { ListFilter } from "../../Dashboard/Filters/ListFilter";
import { ItemSummary } from "../AnalyticsProductPicker";
import { ReportDatePicker } from "../ReportDatePicker";

export const GratuitiesFilter = () => {
    const seller = useSeller();
    const exportModal = useModal(false);

    const { data: guides } = useAutoFetchCollection<Guide>(["sellers", seller.id, "guides"]);

    const [from, to] = useGratuitiesFilterStore((state) => [state.getFrom(), state.getTo()], shallow);
    const [setFrom, setTo] = useGratuitiesFilterStore((state) => [state.setFrom, state.setTo], shallow);
    const [filters, setFilters] = useGratuitiesFilterStore((state) => [state.filters, state.setFilters], shallow);

    const [value, setValue] = useState(getInitialDate(from, to));
    const handleDateChange = (dateValue: JsDateRange) => {
        if (dateValue.from && dateValue.to) {
            setFrom(now(dateValue.from));
            setTo(now(dateValue.to));
        }

        setValue(dateValue);
    };

    return (
        <>
            <div className="flex flex-row items-center justify-between border-b border-gray-light px-6 py-3">
                <div
                    className="flex w-full flex-row items-center justify-start space-x-1 xl:space-x-4"
                    data-testid="gratuity-report-filter"
                >
                    <div>
                        <ReportDatePicker from={from} to={to} value={value} max={now()} onChange={handleDateChange} />
                    </div>
                    <ListFilter
                        label="Guide"
                        title="Guides"
                        target={ItemSummary}
                        value={filters.guides}
                        fetch={() => gratuitiesGuides(guides)}
                        onChange={(guides) => setFilters({ guides })}
                    />
                    <ListFilter
                        label="Status"
                        title="Filter Statuses"
                        target={ItemSummary}
                        value={filters.status}
                        fetch={gratuitiesStatus}
                        onChange={(status) => setFilters({ status })}
                    />
                </div>

                <div className="shrink-0 space-x-2 place-self-end pl-2">
                    <SecondaryButton icon={<ExportIcon />} onClick={exportModal.show}>
                        Export
                    </SecondaryButton>
                </div>
            </div>
            <ExportGratuitiesModal filters={filters} isOpen={exportModal.isVisible} onClose={exportModal.hide} />
        </>
    );
};

const gratuitiesStatus = () => {
    const statuses = (Object.keys(GratuityStatus) as Array<keyof typeof GratuityStatus>).map((key) => {
        return { name: key === "PENDING" ? "Outstanding" : capitalize(key), id: GratuityStatus[key] };
    });

    return { data: statuses, isLoading: false };
};

const gratuitiesGuides = (guides?: Guide[]) => {
    return { data: guides ? guides.map((guide) => ({ name: guide.name, id: guide.id })) : [], isLoading: false };
};

interface ExportGratuitiesModalProps {
    isOpen: boolean;
    onClose: (e: any) => void;
    filters: GratuitiesDataFilter;
}

const ExportGratuitiesModal = ({ isOpen, onClose, filters }: ExportGratuitiesModalProps) => {
    const dateParams = useGratuitiesFilterStore((state) => state.getDateParams());

    const params: any = { ...dateParams, format: "xlsx" };
    if (filters?.status.selected.length > 0) {
        params["status[in]"] = filters.status.selected.join(",");
    }

    if (filters?.guides.selected.length > 0) {
        params["references[in]"] = filters.guides.selected.join(",");
    }

    const { data: job } = useQuery<ExportJob>(
        ["gratuities", params],
        async () => {
            const { data } = await api.get<ExportJob>("/gratuities", { params });
            return data;
        },
        { enabled: isOpen },
    );

    return <ExportJobModal isOpen={isOpen} job={job} onClose={onClose} />;
};
